import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, AlertTitle } from '@mui/material';
import InputBox from '../../atomic/inputbox/InputBox';
import AtomicButton from '../../atomic/button/Button';
import { isIpad, isMobile } from '../../utils/ScreenSizeHelper';
import { isMobileNo } from '../../utils/regexValidation';
import { GlobalStateDataContext } from '../../context-api/data-provider/GlobalStateDataProvider';
import { updateKitchenOperationAPI } from '../../context-api/api-services/Signup.service';
import PHONE_SMS from '../../utils/assets/png/phone_sms_icon_white_bg.png';
import { useNavigate } from 'react-router-dom';

const OTP_EXPIRE_STORAGE_KEY_PREFIX = 'phoneVerifyOtpExpire_';

const getOtpExpireStorageKey = (kitchenName) =>
  `${OTP_EXPIRE_STORAGE_KEY_PREFIX}${kitchenName || ''}`;

const PhoneVerifyOtp = ({ isSignup = false, kitchen_name, phone_no }) => {
  const {
    onLoadInfoResponse,
    setOnLoadInfoResponse,
    isLoggedIn,
    OnPhoneVerify,
    onPhoneVerifyResponse,
  } = useContext(GlobalStateDataContext);
  const navigate = useNavigate();
  const { data, loading, message, success } = useMemo(
    () => onLoadInfoResponse,
    [onLoadInfoResponse]
  );
  const [otp, setOtp] = useState('');
  const [validationError, setValidationError] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [canResend, setCanResend] = useState(false);
  const [showChangePhoneInput, setShowChangePhoneInput] = useState(false);
  const [newPhoneNo, setNewPhoneNo] = useState('');
  const [newPhoneError, setNewPhoneError] = useState(null);
  const [updatePhoneLoading, setUpdatePhoneLoading] = useState(false);

  const targetPhone =
    isSignup && phone_no
      ? `+1${phone_no}`
      : data?.data?.data.KitchenInfo?.phone_no
        ? `+1${data?.data?.data.KitchenInfo?.phone_no}`
        : null;

  useEffect(() => {
    if (isSignup) {
      const payload = {
        kitchen_name: kitchen_name,
        serviceType: 'generateOtp',
        phone_no: targetPhone,
      };

      OnPhoneVerify(payload);
    } else {
      if (isLoggedIn && data?.data?.data.KitchenInfo?.phone_verified_status === false) {
        const kitchenName = data?.data?.data.KitchenInfo?.kitchenName;
        const storageKey = getOtpExpireStorageKey(kitchenName);
        const storedExpire = localStorage.getItem(storageKey);
        const exptime = storedExpire ? parseInt(storedExpire, 10) : null;
        const now = Date.now();

        if (exptime && !Number.isNaN(exptime) && exptime > now) {
          const seconds = Math.floor((exptime - now) / 1000);
          setRemainingSeconds(seconds);
          setCanResend(false);
          return;
        }

        const payload = {
          kitchen_name: kitchenName,
          serviceType: 'generateOtp',
          phone_no: targetPhone,
        };

        OnPhoneVerify(payload);
      }
    }
  }, [onLoadInfoResponse]);

  useEffect(() => {
    const exptime = onPhoneVerifyResponse?.data?.data?.exptime;
    if (exptime) {
      const now = Date.now();
      const seconds = Math.max(0, Math.floor((exptime - now) / 1000));
      setRemainingSeconds(seconds);
      setCanResend(seconds <= 0);

      if (!isSignup && data?.data?.data?.KitchenInfo?.kitchenName) {
        const storageKey = getOtpExpireStorageKey(data.data.data.KitchenInfo.kitchenName);
        localStorage.setItem(storageKey, String(exptime));
      }
    }
  }, [onPhoneVerifyResponse, isSignup, data?.data?.data?.KitchenInfo?.kitchenName]);

  useEffect(() => {
    if (remainingSeconds === null || remainingSeconds <= 0) {
      return;
    }

    const timerId = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev === null || prev <= 1) {
          clearInterval(timerId);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [remainingSeconds]);

  const formatTime = (seconds) => {
    if (seconds === null || seconds < 0) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    const mm = mins.toString().padStart(2, '0');
    const ss = secs.toString().padStart(2, '0');
    return `${mm}:${ss}`;
  };

  const verifyOtp = async () => {
    if (!otp || otp.length !== 5) {
      setValidationError('Please enter the 5 digit verification code.');
      return;
    }

    setValidationError(null);

    const OtpVerifyPayload = isSignup
      ? {
          kitchen_name,
          otpNo: otp,
          serviceType: 'verifyOtpNo',
          phone_no: `+1${phone_no}`,
        }
      : {
          kitchen_name: data?.data?.data.KitchenInfo?.kitchenName,
          otpNo: otp,
          serviceType: 'verifyOtpNo',
          phone_no: `+1${data?.data?.data.KitchenInfo?.phone_no}`,
        };

    const response = await OnPhoneVerify(OtpVerifyPayload);

    if (!isSignup && response?.data?.success) {
      const kitchenName = data?.data?.data?.KitchenInfo?.kitchenName;
      if (kitchenName) {
        localStorage.removeItem(getOtpExpireStorageKey(kitchenName));
      }
      setOnLoadInfoResponse((prev) => {
        if (!prev?.data?.data?.data?.KitchenInfo) {
          return prev;
        }

        return {
          ...prev,
          data: {
            ...prev.data,
            data: {
              ...prev.data.data,
              data: {
                ...prev.data.data.data,
                KitchenInfo: {
                  ...prev.data.data.data.KitchenInfo,
                  phone_verified_status: true,
                },
              },
            },
          },
        };
      });
    }
  };

  const handleResendOtp = () => {
    if (!canResend) return;
    setShowChangePhoneInput(false);
    setOtp('');
    setValidationError(null);
    setCanResend(false);

    if (isSignup) {
      const payload = {
        kitchen_name: kitchen_name,
        serviceType: 'generateOtp',
        phone_no: `+1${phone_no}`,
        // phone_no: '+17739975315',
      };

      OnPhoneVerify(payload);
      return;
    }

    if (isLoggedIn && data?.data?.data.KitchenInfo?.phone_no) {
      const payload = {
        kitchen_name: data?.data?.data.KitchenInfo?.kitchenName,
        serviceType: 'generateOtp',
        phone_no: `+1${data?.data?.data.KitchenInfo?.phone_no}`,
      };

      OnPhoneVerify(payload);
    }
  };

  const handleUpdatePhone = async () => {
    setNewPhoneError(null);
    if (!newPhoneNo || !isMobileNo(newPhoneNo)) {
      setNewPhoneError('Invalid mobile phone number format');
      return;
    }
    const kitchenName = data?.data?.data?.KitchenInfo?.kitchenName;
    if (!kitchenName) return;
    setUpdatePhoneLoading(true);
    try {
      const response = await updateKitchenOperationAPI({
        kitchen_name: kitchenName,
        phone_no: `${newPhoneNo}`,
      });
      const success = response?.data?.success ?? response?.status === 200;
      if (success) {
        setOnLoadInfoResponse((prev) => {
          if (!prev?.data?.data?.data?.KitchenInfo) return prev;
          return {
            ...prev,
            data: {
              ...prev.data,
              data: {
                ...prev.data.data,
                data: {
                  ...prev.data.data.data,
                  KitchenInfo: {
                    ...prev.data.data.data.KitchenInfo,
                    phone_no: newPhoneNo,
                  },
                },
              },
            },
          };
        });
        localStorage.removeItem(getOtpExpireStorageKey(kitchenName));
        setShowChangePhoneInput(false);
        setNewPhoneNo('');
      } else {
        const errorMsg =
          response?.data?.errors?.[0]?.errorMessage ||
          response?.data?.message ||
          'Failed to update phone number';
        setNewPhoneError(errorMsg);
      }
    } catch (err) {
      const errorMsg =
        err?.response?.data?.errors?.[0]?.errorMessage ||
        err?.response?.data?.message ||
        'Failed to update phone number';
      setNewPhoneError(errorMsg);
    } finally {
      setUpdatePhoneLoading(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          backgroundColor: 'white',
          width: '640px',
          height: 'auto',
          borderRadius: '8px',
          boxShadow: 'rgb(189, 189, 189) 0px 3px 6px 0px',
          padding: '32px 40px',
          border: '1px solid rgb(230, 230, 230)',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            height: '60px',
            marginRight: '10px',
            cursor: 'pointer',
          }}
        >
          <img
            src={PHONE_SMS}
            width={180}
            alt='PHONE_SMS'
            loading='eager'
            onError={(e) => {
              console.warn('PHONE_SMS failed to load, retrying...');
              e.target.onerror = null;
              e.target.src = PHONE_SMS + `?v=${Date.now()}`; // force reload
            }}
          />
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
          <div
            style={{
              marginBottom: '20px',
              color: '#3F3F6E',
              fontSize: isIpad ? '24px' : '28px',
              fontWeight: '700',
            }}
          >
            Phone verification
          </div>

          {targetPhone && (
            <div
              style={{
                marginBottom: '8px',
                fontSize: '14px',
                color: '#3F3F6E',
                fontFamily: 'Inter Regular',
              }}
            >
              We have sent a verification code to:{' '}
              <span style={{ fontFamily: 'Inter SemiBold' }}>{targetPhone}</span>
            </div>
          )}

          {validationError && (
            <Alert
              severity='error'
              sx={{
                width: '385px',
                border: 'solid 1px #E74C3C',
                marginBottom: '10px',
                backgroundColor: '#FDEDEB',
                color: 'red',
              }}
            >
              <AlertTitle sx={{ fontSize: '14px', fontWeight: 600 }}>Error</AlertTitle>
              <ul>
                <li style={{ fontSize: '12px' }}>
                  <strong>{validationError}</strong>
                </li>
              </ul>
            </Alert>
          )}
          {onPhoneVerifyResponse?.message && (
            <Alert
              severity={onPhoneVerifyResponse?.success ? 'success' : 'error'}
              sx={{
                width: '385px',
                border: onPhoneVerifyResponse?.success
                  ? 'solid 1px #04cc3dff'
                  : 'solid 1px #ff4430ff',
                marginBottom: '10px',
                backgroundColor: onPhoneVerifyResponse?.success ? '#E8F5E9' : '#FDEDEB',
                color: onPhoneVerifyResponse?.success ? '#2E7D32' : '#f3a097ff',
              }}
            >
              <AlertTitle sx={{ fontSize: '14px', fontWeight: 600 }}>
                {onPhoneVerifyResponse?.success ? 'Success' : 'Error'}
              </AlertTitle>
              <div style={{ fontSize: '12px' }}>{onPhoneVerifyResponse?.message}</div>
            </Alert>
          )}

          <div
            style={{
              marginBottom: '10px',
              fontSize: '14px',
              color: '#3F3F6E',
              fontFamily: 'Inter Regular',
            }}
          >
            Enter the 5 digit verification code you received to confirm your phone number.
          </div>

          <InputBox
            type='number'
            label='Verification code'
            size='small'
            sx={{
              width: '400px',
              height: 'auto',
              margin: '10px 5px 20px 0px',
            }}
            name='otp'
            value={otp}
            maxLength='5'
            onChange={(event) => {
              event.preventDefault();
              const value = event.target.value;

              if (value?.length <= 5) {
                setOtp(value);
              }
            }}
          />

          <AtomicButton
            label='Verify OTP'
            handleSubmit={verifyOtp}
            style={{ backgroundColor: '#181436', width: '90%' }}
            loading={loading}
            disabled={
              !otp || otp.length !== 5 || onPhoneVerifyResponse?.message === 'Invalid Phone Number'
            }
          />
          <div
            style={{
              marginTop: '12px',
              fontSize: '13px',
              color: '#3F3F6E',
              fontFamily: 'Inter Regular',
            }}
          >
            {isSignup && onPhoneVerifyResponse?.message === 'Invalid Phone Number' && (
              <div
                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '12px' }}
              >
                <span style={{ fontFamily: 'Inter Bold', fontWeight: 700, color: '#ff4430ff' }}>
                  Please login to change your phone number.&nbsp;
                  <span
                    onClick={() => navigate('/login')}
                    style={{ color: '#5078e6', cursor: 'pointer', fontFamily: 'Inter SemiBold' }}
                  >
                    Login&nbsp;?
                  </span>
                </span>
              </div>
            )}

            {!isSignup && (
              <>
                <span style={{ fontFamily: 'Inter Regular' }}>
                  {showChangePhoneInput
                    ? 'Enter the new phone number'
                    : onPhoneVerifyResponse?.message === 'Invalid Phone Number'
                      ? 'Invalid Phone Number. '
                      : 'Change phone number. '}
                </span>
                {!showChangePhoneInput ? (
                  <span
                    onClick={() => {
                      setShowChangePhoneInput(true);
                      setNewPhoneError(null);
                      setNewPhoneNo('');
                    }}
                    style={{
                      fontFamily: 'Inter Bold',
                      fontWeight: 700,
                      color: '#ff4430ff',
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Update Now ?
                  </span>
                ) : (
                  <div style={{ marginTop: '12px' }}>
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'row',
                        gap: '12px',
                      }}
                    >
                      <InputBox
                        type='number'
                        label='Mobile phone number'
                        size='small'
                        sx={{
                          width: '200px',
                          margin: 0,
                          '& .MuiInputBase-root': { height: '50px' },
                          height: '50px',
                        }}
                        name='newPhoneNo'
                        value={newPhoneNo}
                        maxLength='10'
                        error={newPhoneError !== null && newPhoneError.length > 0}
                        onChange={(e) => {
                          e.preventDefault();
                          const val = e.target.value;
                          if (val?.length <= 10) {
                            setNewPhoneNo(val);
                            setNewPhoneError(null);
                          }
                        }}
                      />
                      <AtomicButton
                        label='Update'
                        handleSubmit={handleUpdatePhone}
                        style={{
                          backgroundColor: '#181436',
                          width: '140px',
                          minWidth: '140px',
                          height: '50px',
                          margin: 0,
                        }}
                        loadingButtonStyle={{
                          width: '140px',
                          minWidth: '140px',
                          height: '50px',
                          margin: 0,
                        }}
                        loading={updatePhoneLoading}
                        disabled={!newPhoneNo || newPhoneNo.length !== 10}
                      />
                    </div>
                    <div
                      style={{
                        minHeight: '20px',
                        fontSize: '12px',
                        color: '#ff4430ff',
                        marginTop: '4px',
                      }}
                    >
                      {newPhoneError || ''}
                    </div>
                  </div>
                )}
              </>
            )}

            {onPhoneVerifyResponse?.message !== 'Invalid Phone Number' && (
              <span style={!isSignup ? { display: 'block', marginTop: '8px' } : undefined}>
                {remainingSeconds !== null && remainingSeconds > 0 ? (
                  <span>
                    Resend OTP in{' '}
                    <span style={{ fontFamily: 'Inter Bold' }}>
                      {' '}
                      {formatTime(remainingSeconds)}
                    </span>
                  </span>
                ) : (
                  <span>You can resend the OTP now.</span>
                )}
                <span
                  onClick={handleResendOtp}
                  style={{
                    marginLeft: '8px',
                    color: canResend ? '#5078e6' : '#A0A0A0',
                    cursor: canResend ? 'pointer' : 'default',
                    fontFamily: 'Inter SemiBold',
                  }}
                >
                  Resend OTP
                </span>
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhoneVerifyOtp;
